@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.service

object ZustandManager {

    // YARD states
    const val ANGELEGT       = 5
    const val KONTROLLIERT   = 10
    const val RESERVIERT     = 15
    const val TOR_ZUGEWIESEN = 20
    const val AM_TOR         = 25
    const val PIN_ABFRAGE    = 30
    const val IN_VERLADUNG   = 35
    const val IN_BEARBEITUNG = 40
    const val TEILVERLADEN   = 45
    const val TEILENTLADEN   = 45
    const val ABGESCHLOSSEN  = 60
    const val STORNIERT      = 70
    const val VERWALTUNGSABSCHLUSS = 80

    private val yardLabels: Map<Int, String> = mapOf(
        ANGELEGT       to "Auftrag angemeldet",
        KONTROLLIERT   to "Kontrolliert",
        RESERVIERT     to "Angekommen",
        TOR_ZUGEWIESEN to "Tor zugewiesen",
        AM_TOR         to "Am Tor",
        PIN_ABFRAGE    to "PIN-Abfrage",
        IN_VERLADUNG   to "In Verladung",
        IN_BEARBEITUNG to "In Bearbeitung",
        TEILVERLADEN   to "Teilverladen",
        ABGESCHLOSSEN  to "Abgeschlossen",
        STORNIERT      to "Storniert",
        VERWALTUNGSABSCHLUSS to "Verwaltungsabschluss"
    )

    private val yardColors: Map<Int, String> = mapOf(
        ANGELEGT       to "#BDBDBD",
        KONTROLLIERT   to "#42A5F5",
        RESERVIERT     to "#FFA726",
        TOR_ZUGEWIESEN to "#AB47BC",
        AM_TOR         to "#5C6BC0",
        PIN_ABFRAGE    to "#29B6F6",
        IN_VERLADUNG   to "#66BB6A",
        IN_BEARBEITUNG to "#7E57C2",
        TEILVERLADEN   to "#FFB300",
        ABGESCHLOSSEN  to "#26C6DA",
        STORNIERT      to "#EF5350",
        VERWALTUNGSABSCHLUSS to "#66BB6A"
    )

    fun textFor(code: Int): String = yardLabels[code] ?: yardLabels[ANGELEGT].orEmpty()
    fun colorHexFor(code: Int): String = yardColors[code] ?: yardColors[ANGELEGT].orEmpty()

    fun textFor(art: String, code: Int): String = when (art) {
        "WE"      -> yardTextFor("WE", code)
        "WA"      -> yardTextFor("WA", code)
        "CR"      -> CR.textFor(code)
        "SUPPORT" -> Support.textFor(code)
        "SCHULUNG" -> Schulung.textFor(code)
        "URLAUB" -> Urlaub.textFor(code)
        "ZEIT"    -> Zeit.textFor(code)
        "MODUL"   -> Modul.textFor(code)
        "TERMIN"  -> Termin.textFor(code)
        "SLOT"    -> Slot.textFor(code)
        "CHAT"    -> Chat.textFor(code)
        "PAL"     -> PAL.textFor(code)
        else      -> textFor(code)
    }

    fun colorHexFor(art: String, code: Int): String = when (art) {
        "CR"      -> CR.hexFor(code)
        "SUPPORT" -> Support.colorHexFor(code)
        "SCHULUNG" -> Schulung.colorHexFor(code)
        "URLAUB" -> Urlaub.colorHexFor(code)
        "ZEIT"    -> Zeit.colorHexFor(code)
        "MODUL"   -> Modul.colorHexFor(code)
        "TERMIN"  -> Termin.colorHexFor(code)
        "SLOT"    -> Slot.colorHexFor(code)
        "CHAT"    -> Chat.colorHexFor(code)
        "PAL"     -> PAL.colorHexFor(code)
        else      -> colorHexFor(code)
    }

    private fun yardTextFor(art: String, code: Int): String =
        if (art == "WE") {
            when (code) {
                IN_VERLADUNG -> "In Entladung"
                TEILENTLADEN -> "Teilentladen"
                else -> textFor(code)
            }
        } else {
            textFor(code)
        }

    // ── Yard companion helpers (backwards compat) ──────────────────────────
    fun fromCode(code: Int): Int = if (code in yardLabels) code else ANGELEGT
    fun fromText(text: String): Int =
        yardLabels.entries.firstOrNull { it.value.equals(text, ignoreCase = true) }?.key ?: ANGELEGT

    // ── Nested domain managers ─────────────────────────────────────────────

    object Build {
        const val BEREIT = 0
        const val BUILD_GESTARTET = 10
        const val BUILD_WIRD_ERSTELLT = 20
        const val BUILD_ABGESCHLOSSEN = 60
        const val BUILD_FEHLGESCHLAGEN = 70
        const val LIVE_GESTARTET = 110
        const val LIVE_WIRD_EINGESPIELT = 120
        const val LIVE_ABGESCHLOSSEN = 160
        const val LIVE_FEHLGESCHLAGEN = 170

        private val labels = mapOf(
            BEREIT to "Bereit für einen neuen Build",
            BUILD_GESTARTET to "Build gestartet",
            BUILD_WIRD_ERSTELLT to "Build wird erstellt",
            BUILD_ABGESCHLOSSEN to "Build abgeschlossen",
            BUILD_FEHLGESCHLAGEN to "Build fehlgeschlagen",
            LIVE_GESTARTET to "Live-Übernahme gestartet",
            LIVE_WIRD_EINGESPIELT to "Live-Build wird eingespielt",
            LIVE_ABGESCHLOSSEN to "Live-Build abgeschlossen",
            LIVE_FEHLGESCHLAGEN to "Live-Build fehlgeschlagen"
        )

        private val colors = mapOf(
            BEREIT to "#9AA4B5",
            BUILD_GESTARTET to "#FFD54F",
            BUILD_WIRD_ERSTELLT to "#FFD54F",
            BUILD_ABGESCHLOSSEN to "#66BB6A",
            BUILD_FEHLGESCHLAGEN to "#EF5350",
            LIVE_GESTARTET to "#FFD54F",
            LIVE_WIRD_EINGESPIELT to "#FFD54F",
            LIVE_ABGESCHLOSSEN to "#66BB6A",
            LIVE_FEHLGESCHLAGEN to "#EF5350"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else BEREIT
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[BEREIT].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[BEREIT].orEmpty()
        fun isRunning(code: Int): Boolean = code in setOf(BUILD_GESTARTET, BUILD_WIRD_ERSTELLT, LIVE_GESTARTET, LIVE_WIRD_EINGESPIELT)
        fun isSuccessful(code: Int): Boolean = code == BUILD_ABGESCHLOSSEN || code == LIVE_ABGESCHLOSSEN
        fun isFailed(code: Int): Boolean = code == BUILD_FEHLGESCHLAGEN || code == LIVE_FEHLGESCHLAGEN
        fun isFinal(code: Int): Boolean = isSuccessful(code) || isFailed(code)
    }

    object PAL {
        const val IN_KLAERUNG = 15
        const val GEBUCHT = 60
        const val STORNIERT = 70

        private val labels: Map<Int, String> = mapOf(
            IN_KLAERUNG to "In Klärung",
            GEBUCHT to "Gebucht",
            STORNIERT to "Storniert"
        )

        private val colors: Map<Int, String> = mapOf(
            IN_KLAERUNG to "#78909C",
            GEBUCHT to "#66BB6A",
            STORNIERT to "#EF5350"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else IN_KLAERUNG
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[IN_KLAERUNG].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[IN_KLAERUNG].orEmpty()
    }

    object CR {
        const val ANGEFRAGT             = 10
        const val IN_KLAERUNG           = 15
        const val ANGEBOT_UNTERBREITET  = 20
        const val VERBINDLICH           = 30
        const val IN_PROGRAMMIERUNG     = 40
        const val IN_TEST               = 50
        const val BEREIT_ZUR_EINSPIELUNG = 55
        const val EINGESPIELT           = 60
        const val STORNIERT             = 70

        private val labels: Map<Int, String> = mapOf(
            ANGEFRAGT            to "CR Anfrage erstellt",
            IN_KLAERUNG          to "In Klärung",
            ANGEBOT_UNTERBREITET to "Verbindl. Angebot",
            VERBINDLICH          to "Verbindlich beauftragt",
            IN_PROGRAMMIERUNG    to "In Programmierung",
            IN_TEST              to "Zur Abnahme bereit",
            BEREIT_ZUR_EINSPIELUNG to "Bereit zur Einspielung",
            EINGESPIELT          to "Eingespielt",
            STORNIERT            to "Storniert"
        )

        private val colors: Map<Int, String> = mapOf(
            ANGEFRAGT            to "#5b9bff",
            IN_KLAERUNG          to "#78909C",
            ANGEBOT_UNTERBREITET to "#FFA726",
            VERBINDLICH          to "#66BB6A",
            IN_PROGRAMMIERUNG    to "#EC407A",
            IN_TEST              to "#AB47BC",
            BEREIT_ZUR_EINSPIELUNG to "#29B6F6",
            EINGESPIELT          to "#A5D6A7",
            STORNIERT            to "#EF5350"
        )

        fun textFor(code: Int): String = labels[code] ?: labels[ANGEFRAGT].orEmpty()
        fun hexFor(code: Int): String = colors[code] ?: colors[ANGEFRAGT].orEmpty()
        fun isFinal(code: Int): Boolean = code == EINGESPIELT || code == STORNIERT
        fun kundeKannStornieren(code: Int): Boolean = code < VERBINDLICH

        fun validNextCodes(currentCode: Int): List<Int> = when (currentCode) {
            ANGEFRAGT            -> listOf(IN_KLAERUNG)
            IN_KLAERUNG          -> listOf(ANGEBOT_UNTERBREITET)
            ANGEBOT_UNTERBREITET -> listOf(VERBINDLICH, STORNIERT)
            VERBINDLICH -> listOf(IN_PROGRAMMIERUNG)
            IN_PROGRAMMIERUNG -> listOf(IN_TEST)
            IN_TEST -> listOf(BEREIT_ZUR_EINSPIELUNG)
            BEREIT_ZUR_EINSPIELUNG -> listOf(EINGESPIELT)
            else -> emptyList()
        }

        fun isValidTransition(from: Int, to: Int): Boolean =
            to in validNextCodes(from) || (to == STORNIERT && kundeKannStornieren(from))

        val allCodeStrings: List<String> get() = labels.keys.map { it.toString() }
        val allBezeichnungen: List<String> get() = labels.values.toList()

        // legacy compat — used via ZustandManager.CR.State.X.code
        object State {
            const val ANGEFRAGT             = CR.ANGEFRAGT
            const val IN_KLAERUNG           = CR.IN_KLAERUNG
            const val ANGEBOT_UNTERBREITET  = CR.ANGEBOT_UNTERBREITET
            const val VERBINDLICH           = CR.VERBINDLICH
            const val IN_PROGRAMMIERUNG     = CR.IN_PROGRAMMIERUNG
            const val IN_TEST               = CR.IN_TEST
            const val BEREIT_ZUR_EINSPIELUNG = CR.BEREIT_ZUR_EINSPIELUNG
            const val EINGESPIELT           = CR.EINGESPIELT
            const val STORNIERT             = CR.STORNIERT
        }
    }

    object Support {
        const val TICKET_ERSTELLT = 10
        const val OFFEN = 15
        const val IN_BEARBEITUNG  = 20
        const val ABGESCHLOSSEN   = 60

        private val labels: Map<Int, String> = mapOf(
            TICKET_ERSTELLT to "Ticket erstellt",
            OFFEN to "Offen",
            IN_BEARBEITUNG  to "In Bearbeitung",
            ABGESCHLOSSEN   to "Abgeschlossen"
        )

        private val colors: Map<Int, String> = mapOf(
            TICKET_ERSTELLT to "#5b9bff",
            OFFEN to "#90A4AE",
            IN_BEARBEITUNG  to "#FFA726",
            ABGESCHLOSSEN   to "#66BB6A"
        )

        fun textFor(code: Int): String = labels[code] ?: labels[TICKET_ERSTELLT].orEmpty()
        fun fromText(text: String): Int =
            labels.entries.firstOrNull { it.value.equals(text, ignoreCase = true) }?.key ?: TICKET_ERSTELLT
        fun colorHexFor(code: Int): String = colors[code] ?: colors[TICKET_ERSTELLT].orEmpty()

        val dropdownList: List<String> get() = labels.values.toList()
    }

    object Kunde {
        const val ANGELEGT = 10
        const val INSTALLATION_ABGESCHLOSSEN = 60
        const val STORNIERT = 70
        const val GESPERRT = 80

        private val labels: Map<Int, String> = mapOf(
            ANGELEGT to "Kunde angelegt",
            INSTALLATION_ABGESCHLOSSEN to "Installation abgeschlossen",
            STORNIERT to "Kunde storniert",
            GESPERRT to "Kunde gesperrt"
        )

        private val colors: Map<Int, String> = mapOf(
            ANGELEGT to "#42A5F5",
            INSTALLATION_ABGESCHLOSSEN to "#66BB6A",
            STORNIERT to "#EF5350",
            GESPERRT to "#AB47BC"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else ANGELEGT
        fun fromText(text: String): Int =
            labels.entries.firstOrNull { it.value.equals(text, ignoreCase = true) }?.key ?: ANGELEGT
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[ANGELEGT].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[ANGELEGT].orEmpty()

        val dropdownList: List<String> get() = labels.values.toList()
    }

    object Schulung {
        const val ERSTELLT = 10
        const val EINGELADEN = 20
        const val GESTARTET = 40
        const val ABGESCHLOSSEN = 60

        private val labels: Map<Int, String> = mapOf(
            ERSTELLT to "Schulung erstellt",
            EINGELADEN to "Eingeladen",
            GESTARTET to "Gestartet",
            ABGESCHLOSSEN to "Abgeschlossen"
        )

        private val colors: Map<Int, String> = mapOf(
            ERSTELLT to "#5b9bff",
            EINGELADEN to "#90A4AE",
            GESTARTET to "#FFA726",
            ABGESCHLOSSEN to "#66BB6A"
        )

        fun textFor(code: Int): String = labels[code] ?: labels[ERSTELLT].orEmpty()
        fun colorHexFor(code: Int): String = colors[code] ?: colors[ERSTELLT].orEmpty()
    }

    object Firmenregister {
        const val IN_FREISCHALTUNG = 10
        const val IN_VERIFIZIERUNG = 20
        const val VERIFIZIERT = 30
        const val GESPERRT = 80

        private val labels: Map<Int, String> = mapOf(
            IN_FREISCHALTUNG to "In Freischaltung",
            IN_VERIFIZIERUNG to "In Verifizierung",
            VERIFIZIERT to "Verifiziert",
            GESPERRT to "Gesperrt"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else VERIFIZIERT
        fun fromText(text: String): Int =
            labels.entries.firstOrNull { it.value.equals(text, ignoreCase = true) }?.key ?: VERIFIZIERT
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[VERIFIZIERT].orEmpty()

        val dropdownList: List<String> get() = labels.values.toList()
    }

    object SnapEinladung {
        const val KEY_GENERIERT = 10
        const val KEY_ERFOLGREICH_VERWENDET = 60
        const val KEY_ABGELAUFEN = 70
        private val labels = mapOf(KEY_GENERIERT to "Key generiert", KEY_ERFOLGREICH_VERWENDET to "Key erfolgreich verwendet", KEY_ABGELAUFEN to "Key abgelaufen")
        fun textFor(code: Int): String = labels[code] ?: labels[KEY_GENERIERT].orEmpty()
    }

    object Modul {
        const val AKTIVIERT   = 10
        const val DEAKTIVIERT = 20
        const val WARTUNG     = 30
        const val GESPERRT    = 40

        private val labels: Map<Int, String> = mapOf(
            AKTIVIERT   to "Aktiviert",
            DEAKTIVIERT to "Deaktiviert",
            WARTUNG     to "Wartung",
            GESPERRT    to "Gesperrt"
        )

        private val colors: Map<Int, String> = mapOf(
            AKTIVIERT   to "#66BB6A",
            DEAKTIVIERT to "#8a8f9a",
            WARTUNG     to "#42A5F5",
            GESPERRT    to "#EF5350"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else DEAKTIVIERT
        fun fromText(text: String): Int =
            labels.entries.firstOrNull { it.bezeichnung.equals(text, ignoreCase = true) }?.key ?: DEAKTIVIERT
        fun colorHexFor(code: Int): String = colors[code] ?: colors[DEAKTIVIERT].orEmpty()
        fun textFor(code: Int): String = labels[code] ?: labels[DEAKTIVIERT].orEmpty()

        val dropdownList: List<String> get() = labels.values.toList()

        // legacy compat — used via ZustandManager.Modul.State.X.code
        object State {
            const val AKTIVIERT   = Modul.AKTIVIERT
            const val DEAKTIVIERT = Modul.DEAKTIVIERT
            const val WARTUNG     = Modul.WARTUNG
            const val GESPERRT    = Modul.GESPERRT
            // legacy .code accessor
            @Suppress("UNUSED")
            val AKTIVIERT_CODE   get() = AKTIVIERT
            @Suppress("UNUSED")
            val DEAKTIVIERT_CODE get() = DEAKTIVIERT
        }
    }

    object Termin {
        const val ANGEMELDET = 10
        const val ZUGESAGT = 20
        const val STATTGEFUNDEN = 60
        const val STORNIERT = 70

        private val labels: Map<Int, String> = mapOf(
            ANGEMELDET to "Termin angemeldet",
            ZUGESAGT to "Termin zugesagt",
            STATTGEFUNDEN to "Termin stattgefunden",
            STORNIERT to "Termin storniert"
        )

        private val colors: Map<Int, String> = mapOf(
            ANGEMELDET to "#42A5F5",
            ZUGESAGT to "#66BB6A",
            STATTGEFUNDEN to "#26C6DA",
            STORNIERT to "#EF5350"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else ANGEMELDET
        fun fromText(text: String): Int =
            labels.entries.firstOrNull { it.value.equals(text, ignoreCase = true) }?.key ?: ANGEMELDET
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[ANGEMELDET].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[ANGEMELDET].orEmpty()

        val dropdownList: List<String> get() = labels.values.toList()
    }

    object Urlaub {
        const val BEANTRAGT = 10
        const val IN_BEARBEITUNG = 20
        const val GENEHMIGT = 60
        const val VERWEIGERT = 70
        const val STORNIERT = 80

        private val labels: Map<Int, String> = mapOf(
            BEANTRAGT to "Urlaub beantragt",
            IN_BEARBEITUNG to "Urlaubsantrag in Bearbeitung",
            GENEHMIGT to "Urlaub genehmigt",
            VERWEIGERT to "Urlaub verweigert",
            STORNIERT to "Urlaub storniert"
        )

        private const val NEUTRAL_COLOR = "#9AA4B5"
        private const val GEBUCHT_COLOR = "#FFA726"
        private const val STORNIERT_COLOR = "#EF5350"
        private const val GENEHMIGT_COLOR = "#A5D6A7"
        private const val GENOMMEN_COLOR = "#1B5E20"
        private const val KEINE_BUCHUNG_COLOR = "#9AA4B5"
        private val colors: Map<Int, String> = mapOf(
            BEANTRAGT to NEUTRAL_COLOR,
            IN_BEARBEITUNG to NEUTRAL_COLOR,
            GENEHMIGT to NEUTRAL_COLOR,
            VERWEIGERT to NEUTRAL_COLOR,
            STORNIERT to NEUTRAL_COLOR
        )

        fun fromCode(code: Int): Int = if (code in labels) code else BEANTRAGT
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[BEANTRAGT].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[BEANTRAGT].orEmpty()
        fun keineBuchungColorHex(): String = KEINE_BUCHUNG_COLOR
        fun calendarColorHexFor(code: Int, genommen: Boolean = false): String = when (fromCode(code)) {
            BEANTRAGT,
            IN_BEARBEITUNG -> GEBUCHT_COLOR
            GENEHMIGT -> if (genommen) GENOMMEN_COLOR else GENEHMIGT_COLOR
            VERWEIGERT,
            STORNIERT -> STORNIERT_COLOR
            else -> KEINE_BUCHUNG_COLOR
        }
        val dropdownList: List<String> get() = labels.values.toList()
    }

    object Abwesenheit {
        const val ERFASST = 30

        fun isKrankheit(type: String): Boolean =
            type.trim().lowercase() in setOf("krank", "krankheit", "krankmeldung", "krankenschein")

        fun statusFor(type: String, status: Int): Int =
            if (isKrankheit(type)) ERFASST else status

        fun statusText(type: String, status: Int): String =
            if (isKrankheit(type)) {
                "erfasst"
            } else {
                when (Urlaub.fromCode(status)) {
                    Urlaub.BEANTRAGT -> "beantragt"
                    Urlaub.IN_BEARBEITUNG -> "in Bearbeitung"
                    Urlaub.GENEHMIGT -> "genehmigt"
                    Urlaub.VERWEIGERT -> "abgelehnt"
                    Urlaub.STORNIERT -> "storniert"
                    else -> Urlaub.textFor(status)
                }
            }
    }

    object Zeit {
        const val ANWESEND = 10
        const val FEHLTAG = 20
        const val KRANK = 30
        const val FEIERTAG = 40
        const val URLAUB = 50
        const val FREIZEITAUSGLEICH = 60
        const val SOLL_ERFUELLT = 70
        const val FEHLBUCHUNG = 80
        const val KRANKENSCHEIN = 90

        private val labels: Map<Int, String> = mapOf(
            ANWESEND to "Anwesend",
            FEHLTAG to "Fehltag",
            KRANK to "Krank",
            FEIERTAG to "Feiertag",
            URLAUB to "Urlaub",
            FREIZEITAUSGLEICH to "Freizeitausgleich",
            SOLL_ERFUELLT to "Soll erfüllt",
            FEHLBUCHUNG to "Fehlbuchung",
            KRANKENSCHEIN to "Krankenschein"
        )

        private const val NEUTRAL_COLOR = "#9AA4B5"
        private const val ANWESEND_COLOR = "#A5D6A7"
        private const val FEHLTAG_COLOR = "#FFA726"
        private const val KRANK_COLOR = "#FFD54F"
        private const val FEIERTAG_COLOR = "#42A5F5"
        private const val URLAUB_COLOR = "#FFD54F"
        private const val FREIZEITAUSGLEICH_COLOR = "#42A5F5"
        private const val SOLL_ERFUELLT_COLOR = "#66BB6A"
        private const val FEHLBUCHUNG_COLOR = "#D32539"
        private const val KRANKENSCHEIN_COLOR = "#EF5350"
        private const val KEINE_BUCHUNG_COLOR = "#9AA4B5"
        private val colors: Map<Int, String> = mapOf(
            ANWESEND to ANWESEND_COLOR,
            FEHLTAG to FEHLTAG_COLOR,
            KRANK to KRANK_COLOR,
            FEIERTAG to FEIERTAG_COLOR,
            URLAUB to URLAUB_COLOR,
            FREIZEITAUSGLEICH to FREIZEITAUSGLEICH_COLOR,
            SOLL_ERFUELLT to SOLL_ERFUELLT_COLOR,
            FEHLBUCHUNG to FEHLBUCHUNG_COLOR,
            KRANKENSCHEIN to KRANKENSCHEIN_COLOR
        )

        fun fromCode(code: Int): Int = if (code in labels) code else ANWESEND
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[ANWESEND].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[ANWESEND].orEmpty()
        fun keineBuchungColorHex(): String = KEINE_BUCHUNG_COLOR
        fun calendarColorHexFor(code: Int): String = colorHexFor(code)
        val allCodes: List<Int> get() = labels.keys.toList()
    }

    object Slot {
        const val FREI = 110
        const val IN_BUCHUNG = 120
        const val RESERVIERT = 130
        const val GESPERRT = 140
        const val BELEGT = 150

        private val labels: Map<Int, String> = mapOf(
            FREI to "Frei",
            IN_BUCHUNG to "In Buchung",
            RESERVIERT to "Reserviert",
            GESPERRT to "Gesperrt",
            BELEGT to "Belegt"
        )

        private val colors: Map<Int, String> = mapOf(
            FREI to "#66BB6A",
            IN_BUCHUNG to "#EF5350",
            RESERVIERT to "#42A5F5",
            GESPERRT to "#EF5350",
            BELEGT to "#AB47BC"
        )

        fun fromCode(code: Int): Int = if (code in labels) code else FREI
        fun textFor(code: Int): String = labels[fromCode(code)] ?: labels[FREI].orEmpty()
        fun colorHexFor(code: Int): String = colors[fromCode(code)] ?: colors[FREI].orEmpty()
        fun allCodes(): List<Int> = labels.keys.toList()
    }

    object ModulShop {
        const val KAUFEN = 10
        const val GEKAUFT = 20
        const val INSTALLIERT = 30
        const val AKTUELL = 40
        const val UPDATE_VERFUEGBAR = 50

        private val labels: Map<Int, String> = mapOf(
            KAUFEN to "Kaufen",
            GEKAUFT to "Gekauft",
            INSTALLIERT to "Installiert",
            AKTUELL to "Aktuell",
            UPDATE_VERFUEGBAR to "Update verfügbar"
        )

        fun textFor(code: Int): String = labels[code] ?: labels[KAUFEN].orEmpty()

        fun besitzStatusCode(installed: Boolean, gekauft: Boolean): Int = when {
            installed -> INSTALLIERT
            gekauft -> GEKAUFT
            else -> KAUFEN
        }

        fun besitzStatus(installed: Boolean, gekauft: Boolean): String = when {
            installed -> textFor(INSTALLIERT)
            gekauft -> textFor(GEKAUFT)
            else -> textFor(KAUFEN)
        }

        fun versionsStatusCode(installed: Boolean, updateAvailable: Boolean): Int? = when {
            !installed -> null
            updateAvailable -> UPDATE_VERFUEGBAR
            else -> AKTUELL
        }

        fun versionsStatus(installed: Boolean, updateAvailable: Boolean): String = when {
            !installed -> "-"
            updateAvailable -> textFor(UPDATE_VERFUEGBAR)
            else -> textFor(AKTUELL)
        }

        fun detailStatusCode(installed: Boolean, updateAvailable: Boolean, gekauft: Boolean): Int = when {
            installed && updateAvailable -> UPDATE_VERFUEGBAR
            installed -> INSTALLIERT
            gekauft -> GEKAUFT
            else -> KAUFEN
        }

        fun detailStatus(installed: Boolean, updateAvailable: Boolean, gekauft: Boolean): String = when {
            installed && updateAvailable -> textFor(UPDATE_VERFUEGBAR)
            installed -> textFor(INSTALLIERT)
            gekauft -> textFor(GEKAUFT)
            else -> textFor(KAUFEN)
        }
    }

    object MeineModule {
        const val GEKAUFT = 10
        const val INSTALLIEREN = 20
        const val INSTALLIERT = 30
        const val DEINSTALLIEREN = 40

        private val labels: Map<Int, String> = mapOf(
            GEKAUFT to "Gekauft",
            INSTALLIEREN to "Installieren",
            INSTALLIERT to "Installiert",
            DEINSTALLIEREN to "Deinstallieren"
        )

        fun textFor(code: Int): String = labels[code] ?: labels[INSTALLIEREN].orEmpty()
    }

    object Chat {
        const val AKTIV      = 10
        const val ARCHIVIERT = 60

        fun textFor(code: Int): String = when (code) {
            AKTIV -> "Aktiv"
            ARCHIVIERT -> "Archiviert"
            else -> "Aktiv"
        }

        fun colorHexFor(code: Int): String = when (code) {
            AKTIV -> "#5b9bff"
            ARCHIVIERT -> "#78909C"
            else -> "#5b9bff"
        }
    }
}

private val Map.Entry<Int, String>.bezeichnung get() = value


